import moment from 'moment/moment';
import { setLocale } from 'react-redux-i18n';
import { persistReducer, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist-indexeddb-storage';
import Cookie from 'services/Cookie';
import {
  FLUSH_USER_DATA,
  IMPERSONATION_EXPIRED,
  LOGOUT_USER_PENDING,
  START_IMPERSONATION_SUCCESS,
  VERIFY_REJECTED,
} from 'store/app/auth/action';
import { SET_ARCHIVED_CHANNEL_FULFILLED } from 'store/app/entities/channels/action';
import { ADD_MESSAGE, SEND_MESSAGE_FULFILLED } from 'store/app/entities/messages/action';
import AddRehydratedTransform from 'utils/AddRehydratedTransform';
import {
  clearActiveCompanyId,
  clearTokenLastCompanyId,
  pickDefaultCompany,
  resolveCompanyFromSubdomain,
  resolvePreferredCompanyId,
  setTokenLastCompanyId,
  syncActiveCompanyFromUrl,
} from 'utils/activeCompany';
import {
  clearImpersonationCookie,
  clearToken,
  clearUserData,
  handleLogin,
  setTokenCookie,
} from 'utils/auth';
import computeUserLastCompany from 'utils/computeUserLastCompany';
import getSubdomain from 'utils/getSubdomain';
import { parseMeResponse } from 'utils/mePayload';
import { writeLocaleCookie } from 'utils/localeCookie';

function createAuthInitialState({ restoreImpersonationFromCookie = false } = {}) {
  const impersonateToken = restoreImpersonationFromCookie ? Cookie.get('impersonate') : null;
  return {
    token: Cookie.get('token'),
    duration: null,
    expiresAt: null,
    company_id: null,
    error: null,
    fulfilledTimeStamp: null,
    isError: false,
    isFetching: false,
    isLoading: false,
    isSuccess: false,
    isUninitialized: true,
    loading: false,
    startedTimeStamp: null,
    rehydrated: false,
    // Boot-only: restore impersonation across reloads / subdomain hops from the shared cookie.
    impersonation: impersonateToken?.hash ? { token: impersonateToken } : null,
  };
}

const bootState = createAuthInitialState({ restoreImpersonationFromCookie: true });

const getCompany = user => {
  const { _id } = computeUserLastCompany(user);
  return user?.companies?.find(c => c._id === _id) || { employee: { _id: 'x' } };
};

const ROLE_SUPER_ADMIN = 2;

const authReducer = (
  state = { ...bootState },
  { type, key, payload, meta, asyncDispatch } = {},
) => {
  switch (type) {
    case 'GET_USER_DATA_PENDING':
      return {
        ...state,
        loading: !!state.isUninitialized,
        isLoading: !!state.isUninitialized,
        startedTimeStamp: Date.now(),
        isFetching: true,
      };

    case 'GET_USER_DATA_FULFILLED': {
      const { user: meUser, tokenLastCompanyId } = parseMeResponse(payload);
      setTokenLastCompanyId(tokenLastCompanyId);
      const preferredCompanyId = resolvePreferredCompanyId(tokenLastCompanyId, state.company_id);
      const company = pickDefaultCompany(meUser, preferredCompanyId) || getCompany(meUser);
      const { employee } = company;
      const fromUrl = resolveCompanyFromSubdomain(meUser.companies, getSubdomain());
      const resolvedCompanyId =
        fromUrl?._id ||
        pickDefaultCompany(meUser, preferredCompanyId)?._id ||
        meUser.companies[0]._id;
      syncActiveCompanyFromUrl(meUser.companies);
      return {
        ...state,
        loading: false,
        isLoading: false,
        user_id: meUser._id,
        employee_id: employee?._id,
        isSuperAdmin: meUser.role === ROLE_SUPER_ADMIN,
        company_id: resolvedCompanyId,
        token_last_company_id: tokenLastCompanyId,
        employee: employee && {
          ...employee,
          company_id: employee.company_id || company._id || resolvedCompanyId,
          name: [employee.firstName, employee.surName].filter(Boolean).join(' '),
          user: {
            ...(typeof employee.user === 'string' ? { _id: employee.user } : employee.user),
            ...meUser,
            isSuperAdmin: meUser.role === ROLE_SUPER_ADMIN,
          },
        },
        fulfilledTimeStamp: Date.now(),
        isFetching: false,
        isUninitialized: false,
        error: null,
        isError: false,
        isSuccess: true,
        rehydrated: false,
      };
    }

    case 'GET_USER_DATA_REJECTED':
      return {
        ...state,
        loading: false,
        isLoading: false,
        rejected: true,
        error: payload,
        fulfilledTimeStamp: null,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };

    case 'LOGIN_USER_FULFILLED': {
      const { token, user, loginMethod, passkeyEnrollmentCompany, passkeyEnrollmentHandoff } =
        payload;
      handleLogin(
        token,
        user,
        meta.keepSessionOpen,
        meta.returnUrl,
        loginMethod,
        passkeyEnrollmentCompany,
        passkeyEnrollmentHandoff,
      );
      return { ...state, token, impersonation: null };
    }

    case FLUSH_USER_DATA: {
      clearUserData();
      if (!state.impersonation) {
        clearActiveCompanyId();
        clearTokenLastCompanyId();
      }
      return {
        ...createAuthInitialState(),
        token: state.token || Cookie.get('token'),
        impersonation: state.impersonation,
        isUninitialized: true,
      };
    }

    case LOGOUT_USER_PENDING: {
      clearUserData();
      clearActiveCompanyId();
      clearTokenLastCompanyId();
      clearImpersonationCookie();
      return createAuthInitialState();
    }

    case VERIFY_REJECTED: {
      clearToken();
      clearImpersonationCookie();
      return createAuthInitialState();
    }

    case START_IMPERSONATION_SUCCESS: {
      clearTokenLastCompanyId();
      const { token, targetUser, expiresAt, superadminUser } = payload;
      return {
        ...state,
        impersonation: {
          token,
          targetUser,
          expiresAt,
          superadminUser,
        },
      };
    }

    case IMPERSONATION_EXPIRED:
      return {
        ...state,
        impersonation: null,
      };

    case 'TOKEN_REFRESHED': {
      const { token, keepSessionOpen } = payload;
      setTokenCookie(keepSessionOpen, token);
      return { ...state, token };
    }

    case ADD_MESSAGE:
    case SEND_MESSAGE_FULFILLED: {
      let archivedChannels = state.employee?.archivedChannels || [];
      const wasArchived = archivedChannels.includes(payload.channel);
      if (wasArchived) {
        archivedChannels = archivedChannels.filter(c => c !== payload.channel);
        return { ...state, employee: { ...state.employee, archivedChannels } };
      }
      return state;
    }

    case SET_ARCHIVED_CHANNEL_FULFILLED: {
      let archivedChannels = state.employee?.archivedChannels || [];
      const wasArchived = archivedChannels.includes(meta.channelId);
      if (!!meta.archived !== !!wasArchived) {
        if (meta.archived) {
          archivedChannels = [...archivedChannels, meta.channelId];
        } else {
          archivedChannels = archivedChannels.filter(c => c !== meta.channelId);
        }
        return { ...state, employee: { ...state.employee, archivedChannels } };
      }
      return state;
    }

    case REHYDRATE: {
      if (key === 'auth') {
        const language = payload?.employee?.settings?.language;
        if (language) {
          moment().locale(language);
          moment.locale(language);
          asyncDispatch(setLocale(language));
          writeLocaleCookie(language);
        }
        if (payload?.token_last_company_id) {
          setTokenLastCompanyId(payload.token_last_company_id);
        }
      }
      return state;
    }

    default:
      return state;
  }
};

const persistConfig = {
  key: 'auth',
  blacklist: ['token', 'impersonation', 'isFetching', 'isLoading', 'isError', 'loading'],
  storage: storage('ommnio'),
  throttle: 200,
  version: 0,
  transforms: [AddRehydratedTransform],
};

export default persistReducer(persistConfig, authReducer);
